/**
 * KAN-743: таблица товаров каталога: миниатюра, название (ссылка на карточку),
 * артикул, категория, цена, наличие, остаток. Широкая таблица прокручивается
 * внутри своей рамки, страница горизонтально не прокручивается.
 * KAN-761: у товара в продаже кнопка "В заказ" (диалог AddToOrderDialog).
 */
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { canOrderProduct, useAddToOrder } from '@/hooks/useAddToOrder';
import { formatPrice } from '@/lib/catalogTree';
import type { CatalogProduct } from '@/types/catalog';
import { AddToOrderButton, AddToOrderFlowDialogs } from './AddToOrderDialog';
import { ManualBadge } from './ManualBadge';
import { ProductThumb } from './ProductThumb';

interface CatalogProductsTableProps {
  products: CatalogProduct[];
  /** Адрес списка, куда вернёт кнопка "Назад" карточки. */
  backTo: string;
}

export function AvailabilityBadge({ available }: { available: boolean }) {
  return (
    <Badge variant={available ? 'completed' : 'secondary'} className="whitespace-nowrap">
      {available ? 'В наличии' : 'Нет в наличии'}
    </Badge>
  );
}

export function CatalogProductsTable({ products, backTo }: CatalogProductsTableProps) {
  const addToOrder = useAddToOrder();
  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <Table aria-label="Товары">
        <TableHeader>
          <TableRow>
            <TableHead className="w-14" />
            <TableHead>Название</TableHead>
            <TableHead>Артикул</TableHead>
            <TableHead>Категория</TableHead>
            <TableHead className="text-right">Цена</TableHead>
            <TableHead>Наличие</TableHead>
            <TableHead className="text-right">Остаток</TableHead>
            <TableHead>
              <span className="sr-only">Заказ</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => (
            <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
              <TableCell className="py-2">
                <ProductThumb src={product.picture_url} alt="" className="h-10 w-10" />
              </TableCell>
              <TableCell className="min-w-[200px] max-w-[360px]">
                <Link
                  to={`/catalog/products/${encodeURIComponent(String(product.id))}`}
                  state={{ backTo }}
                  className="line-clamp-2 font-medium text-blue-600 hover:underline"
                >
                  {product.name}
                </Link>
                {product.manual && (
                  <div className="mt-1">
                    <ManualBadge />
                  </div>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm text-gray-600">{product.sku || '-'}</TableCell>
              <TableCell className="max-w-[200px] truncate text-sm text-gray-600" title={product.category_name ?? ''}>
                {product.category_name || '-'}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right text-sm font-medium">
                {formatPrice(product.price, product.currency)}
              </TableCell>
              <TableCell>
                <AvailabilityBadge available={product.available} />
              </TableCell>
              <TableCell className="text-right text-sm tabular-nums text-gray-600">
                {product.stock_quantity ?? '-'}
              </TableCell>
              <TableCell className="text-right">
                {canOrderProduct(product) && (
                  <AddToOrderButton label={`В заказ: ${product.name}`} onClick={() => addToOrder.open(product)} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <AddToOrderFlowDialogs flow={addToOrder} />
    </div>
  );
}
